/**
 * Analytics Module
 * Handles event tracking and analytics functionality
 */
import type { AnalyticsEvent, ImageData, CookieConsent } from '../types';

export class Analytics {
    appName: string;
    appVersion: string;
    isDevelopment: boolean;
    analyticsEnabled: boolean;

    constructor(appName: string = 'ImageCraft', appVersion: string = '1.0') {
        this.appName = appName;
        this.appVersion = appVersion;
        this.isDevelopment = !window.location.href.includes('https://');
        this.analyticsEnabled = false;
        
        // Listen for cookie consent changes
        window.addEventListener('cookieConsentChanged', (event: Event) => {
            const customEvent = event as CustomEvent<CookieConsent>;
            this.updateConsent(customEvent.detail);
        });
    }

    /**
     * Update analytics consent status
     * @param consent - Cookie consent object
     */
    updateConsent(consent: CookieConsent): void {
        this.analyticsEnabled = consent.analytics;
        
        if (this.isDevelopment) {
            console.log('Analytics consent updated:', this.analyticsEnabled);
        }
    }

    /**
     * Check if analytics tracking is allowed
     */
    isTrackingAllowed(): boolean {
        // Check if we have a global cookie consent controller
        if (window.cookieConsent && typeof window.cookieConsent.isCookieEnabled === 'function') {
            return window.cookieConsent.isCookieEnabled('analytics');
        }
        
        // Fallback to internal analytics enabled flag
        return this.analyticsEnabled;
    }

    /**
     * Track an event with Google Analytics 4 and console logging
     * @param eventName - Name of the event to track
     * @param parameters - Additional parameters for the event
     */
    trackEvent(eventName: string, parameters: AnalyticsEvent = {}): void {
        // Only track if analytics is enabled
        if (!this.isTrackingAllowed()) {
            if (this.isDevelopment) {
                console.log('Analytics tracking disabled, event not sent:', eventName, parameters);
            }
            return;
        }
        
        // Google Analytics 4 event tracking
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', eventName, {
                app_name: this.appName,
                app_version: this.appVersion,
                ...parameters
            });
        }
        
        // Console log for development
        if (this.isDevelopment) {
            console.log('Event:', eventName, parameters);
        }
    }

    /**
     * Track app initialization
     */
    trackAppInitialized(): void {
        this.trackEvent('app_initialized', {
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
        });
    }

    /**
     * Track tab switching
     * @param tabName - Name of the tab that was switched to
     */
    trackTabSwitch(tabName: string): void {
        this.trackEvent('tab_switched', { tab: tabName });
    }

    /**
     * Track format selection
     * @param format - The selected format (png, jpg, webp, etc.)
     */
    trackFormatSelection(format: string): void {
        this.trackEvent('format_selected', { format });
    }

    /**
     * Track image generation and download
     * @param imageData - Data about the generated image
     */
    trackImageGeneratedAndDownloaded(imageData: ImageData): void {
        this.trackEvent('image_generated_and_downloaded', {
            width: imageData.width,
            height: imageData.height,
            format: imageData.format,
            has_background_image: imageData.hasBackgroundImage,
            has_text: imageData.hasText,
            generation_time: imageData.generationTime
        });
    }

    /**
     * Track image export
     * @param exportData - Data about the exported image
     */
    trackImageExported(exportData: ImageData): void {
        this.trackEvent('image_exported', {
            width: exportData.width,
            height: exportData.height,
            format: exportData.format,
            file_size: exportData.fileSize
        });
    }

    /**
     * Track export errors
     * @param error - Error message
     */
    trackExportError(error: string): void {
        this.trackEvent('export_error', { error });
    }

    /**
     * Track form clearing
     */
    trackFormCleared(): void {
        this.trackEvent('form_cleared');
    }

    /**
     * Track background image upload
     * @param imageData - Data about the uploaded image
     */
    trackBackgroundImageUploaded(imageData: { fileSize: number; fileType: string; width: number; height: number }): void {
        this.trackEvent('background_image_uploaded', {
            file_size: imageData.fileSize,
            file_type: imageData.fileType,
            image_width: imageData.width,
            image_height: imageData.height
        });
    }

    /**
     * Track background image removal
     */
    trackBackgroundImageRemoved(): void {
        this.trackEvent('background_image_removed');
    }

    /**
     * Track color changes
     * @param colorType - Type of color changed (background, text)
     * @param color - The color value
     */
    trackColorChange(colorType: string, color: string): void {
        this.trackEvent('color_changed', {
            color_type: colorType,
            color_value: color
        });
    }

    /**
     * Track errors
     * @param errorType - Type of error
     * @param message - Error message
     */
    trackError(errorType: string, message: string): void {
        this.trackEvent('error_occurred', {
            error_type: errorType,
            error_message: message,
            url: window.location.href,
            timestamp: new Date().toISOString()
        });
    }

    /**
     * Track dimension changes (width/height)
     * @param dimension - Which dimension changed ('width' or 'height')
     * @param value - The new dimension value
     */
    trackDimensionChange(dimension: string, value: number): void {
        this.trackEvent('dimension_changed', {
            dimension_type: dimension,
            value: value
        });
    }

    /**
     * Track text content changes
     * @param charCount - Character count in the text
     */
    trackTextChange(charCount: number): void {
        this.trackEvent('text_content_changed', {
            character_count: charCount,
            has_text: charCount > 0
        });
    }

    /**
     * Track font property changes
     * @param property - Font property ('size' or 'family')
     * @param value - The new value
     */
    trackFontChange(property: string, value: string | number): void {
        this.trackEvent('font_changed', {
            font_property: property,
            value: value
        });
    }

    /**
     * Track clear button click
     */
    trackClearButtonClicked(): void {
        this.trackEvent('clear_button_clicked');
    }

    /**
     * Track generate button click
     * @param config - Current configuration
     */
    trackGenerateButtonClicked(config: { width: number; height: number; format: string; text?: string; backgroundImage?: any }): void {
        this.trackEvent('generate_button_clicked', {
            width: config.width,
            height: config.height,
            format: config.format,
            has_text: !!config.text && config.text.length > 0,
            has_background_image: !!config.backgroundImage
        });
    }
}
